import React, { useState, useEffect } from 'react';
import { styled } from '@linaria/react';
import { useTheme, ITheme } from 'src/theme-revamp';

import tickWhiteIcon from '@assets/icon/tick-white.svg';

const HiddenCheckbox = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
`;

const IconElement = styled.img<CheckboxProps>`
  width: 20px;
  height: 20px;
  position: relative;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  top: 2px;
`;

const CircleElement = styled.div<CheckboxProps>`
  width: 16px;
  height: 16px;
  position: absolute;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  background: ${(props) => props.newRedio ? "#883E89" : props.theme?.colors.grey[1100]};
  border-radius: 20px;
  z-index: 100;
  top:  ${(props) => props.newRedio? "4.2px" :"6px"};
  left: ${(props) => props.newRedio? "4.5px" :"6px"};
`;

const StyledCheckbox = styled.div<CheckboxProps>`
  cursor: ${(prop) => (prop.disabled ? 'default' : 'pointer')};
  position: relative;
  padding: 0 2px;
  height: 24px;
  background: ${(props) => (props.checked ? '#883E89' : '#883E89')};
  border-radius: ${(props) => (props.type === 'radio' ? '30px' : '4px')};
  transition: all 150ms;
  background-color: #f5f5f5;
  ${IconElement} {
    visibility: ${(props) =>
      props.checked
        ? props.type !== 'radio'
          ? 'visible'
          : 'hidden'
        : 'hidden'};
  }
  ${CircleElement} {
    visibility: ${(props) =>
      props.checked && props.type === 'radio' ? 'visible' : 'hidden'};
  }
  align-self: ${(props)=> props.checkboxAlignment ? props.checkboxAlignment : "flex-start"};
  box-shadow: none;
  text-align: center;
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  border: 1px solid ${(props) => 
    props.newRedio ? 
      props.checked 
      ? '#883E89'
      : '#D8D8D8' 
    : 0
  };
`;


const CheckListItemStyled = styled.div<CheckboxProps>`
  display: flex;
  align-items: center;
  column-gap: 16px;
  cursor:pointer;
`;

const CheckListItemText = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  color: #434343;
`;
const Content = styled.div<CheckboxProps>`
  font-size: 16px;
  margin-left: 14px;
  line-height: 24px;
  transform: ${(props) =>
    props.variant === 'border' ? 'translateY(4px)' : 'translateY(2px)'};
  & p {
    color: ${(props) => props.theme?.colors.grey[500]};
    font-weight: ${(props) =>
      props.checked && props.variant === 'border' ? 'bold' : 'normal'};
  }
`;

const Container = styled.div<CheckboxProps>`
  flex-shrink: 1;
  display: flex;
  vertical-align: middle;
  padding: ${(props) => (props.variant === 'border' ? 8 : 0)}px;
  border: 1px solid
    ${(props) =>
      props.variant === 'border'
        ? props.checked
          ? props.theme?.colors.grey[1100]
          : props.theme?.colors.grey[100]
        : 'none'};
  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 3px green;
  }
  ${Content} p {
    color: ${(props) =>
      props.variant === 'border' && props.checked
        ? props.theme?.colors.white
        : props.theme?.colors.grey[500]};
  }
  ${StyledCheckbox} {
    background: ${(props) =>
      props.newRedio ? "none" 
        : (props.variant === 'border' && props.checked)
        ? props.theme?.colors.white || '#F5F5F5'
        : props.checked
        ? props.theme?.colors ? props.theme?.colors.primary : ""
        : props.isWhiteCheckbox ? '#fff' : '#F5F5F5'};
  }
  ${Content} p {
    color: ${(props) =>
      props.variant === 'border' && props.checked
        ? props.theme?.colors.white
        : props.theme?.colors.grey[500]};
  }
  border-radius: 4px;
  background-color: ${(props) =>
    props.variant === 'border'
      ? props.checked
        ? props.theme?.colors.grey[1100]
        : props.theme?.colors.grey[200]
      : 'unset'};
  margin: ${(props) =>
    typeof props.margin === 'number'
      ? `${props.margin || 0}px`
      : props.margin || 0};
  box-shadow: ${(props) =>
    props.variant === 'border'
      ? props.checked
        ? 'none'
        : '0 2px 2px 0 rgba(204, 204, 204, 0.5)'
      : 'none'};
  cursor: ${(props) => (props.variant === 'border' ? 'pointer' : 'default')};
  transition: background-color 0.5s, border-color 0.5s;
`;

export interface CheckboxProps {
  variant?: 'default' | 'border';
  type?: 'default' | 'radio';
  theme?: ITheme;
  className?: string;
  checked?: boolean;
  children?: React.ReactNode;
  margin: number | string;
  name?: string;
  disabled?: boolean;
  onChange?: (e: any) => void;
  checkboxAlignment?: string
  newRedio?: boolean;
  isWhiteCheckbox?: boolean;
}

function Checkbox({
  variant,
  children,
  checked,
  margin,
  onChange,
  name,
  disabled = false,
  className,
  checkboxAlignment,
  newRedio,
  isWhiteCheckbox,
  ...props
}: CheckboxProps) {
  const [value, change] = useState<boolean | undefined>(checked);
  const handleChange = (e: any) => {
    if (disabled) return;
    const nextValue = !value;
    change(nextValue);
    if (onChange) {
      onChange({ ...e, target: { ...e.target, name, value: nextValue } });
    }
  };

  useEffect(() => {
    if (checked !== undefined && value !== checked) {
      change(checked);
    }
  }, [value, checked]);

  const theme: ITheme = useTheme();
  return (
   
    <Container
      className={className}
      theme={theme}
      variant={variant}
      checked={value}
      margin={margin}
      onClick={variant === 'border' ? handleChange : undefined}
      newRedio={newRedio}
      isWhiteCheckbox={isWhiteCheckbox}
    >
      <CheckListItemStyled onClick={handleChange} name={name} margin={''}>
        <HiddenCheckbox />
        <StyledCheckbox
          className="checkbox"
          name={name}
          theme={theme}
          disabled={disabled}
          variant={variant}
          checked={value}
          {...props}
          margin={margin}
          checkboxAlignment={checkboxAlignment}
          newRedio={newRedio}
        >
          <IconElement src={tickWhiteIcon} margin={margin}></IconElement>
          <CircleElement margin={margin} theme={theme} newRedio={newRedio}></CircleElement>
        </StyledCheckbox>
        <CheckListItemText>{children}</CheckListItemText>
      </CheckListItemStyled>
    </Container>
  );
}

Checkbox.defaultProps = {
  variant: 'default',
  margin: 0,
  onChange: () => {},
};

export { Checkbox };
